/* BMK's Free Flight Store — all theme behaviour, as custom elements.
   No framework, no jQuery, no build step. */

(function () {
  'use strict';

  const money = (cents) =>
    new Intl.NumberFormat(document.documentElement.lang || 'en-GB', {
      style: 'currency',
      currency: window.Shopify?.currency?.active || 'GBP',
    }).format(cents / 100);

  /* ---------------------------------------------------------------- drawer */

  class DrawerElement extends HTMLElement {
    connectedCallback() {
      this.opener = document.querySelector(`[aria-controls="${this.id}"]`);
      this.addEventListener('click', (e) => {
        if (e.target.closest('[data-drawer-close]') || e.target.matches('.drawer__overlay')) {
          this.close();
        }
      });
      document.addEventListener('keydown', (e) => {
        if (e.key === 'Escape' && this.classList.contains('is-open')) this.close();
      });
      this.opener?.addEventListener('click', () => this.open());
    }

    open() {
      this.classList.add('is-open');
      this.opener?.setAttribute('aria-expanded', 'true');
      document.body.style.overflow = 'hidden';
      this.querySelector('input, button, [href]')?.focus();
    }

    close() {
      this.classList.remove('is-open');
      this.opener?.setAttribute('aria-expanded', 'false');
      document.body.style.overflow = '';
      this.opener?.focus();
    }
  }
  customElements.define('drawer-element', DrawerElement);

  /* ------------------------------------------------------- menu toggle */

  class MenuToggle extends HTMLElement {
    connectedCallback() {
      this.button = this.querySelector('button');
      this.nav = document.getElementById(this.dataset.target);
      this.button?.addEventListener('click', () => {
        const open = this.nav.classList.toggle('is-open');
        this.button.setAttribute('aria-expanded', String(open));
      });
    }
  }
  customElements.define('menu-toggle', MenuToggle);

  /* ------------------------------------------------- predictive search */

  class PredictiveSearch extends HTMLElement {
    connectedCallback() {
      this.input = this.querySelector('input[type="search"]');
      this.results = this.querySelector('[data-results]');
      this.abort = null;

      let timer;
      this.input?.addEventListener('input', () => {
        clearTimeout(timer);
        timer = setTimeout(() => this.search(this.input.value.trim()), 250);
      });
    }

    async search(term) {
      if (term.length < 2) {
        this.results.innerHTML = '';
        return;
      }
      this.abort?.abort();
      this.abort = new AbortController();

      const url = `${window.routes.predictive_search_url}?q=${encodeURIComponent(term)}` +
        `&resources[type]=product&resources[limit]=6&section_id=predictive-search`;

      try {
        const res = await fetch(url, { signal: this.abort.signal });
        if (!res.ok) throw new Error(res.statusText);
        const text = await res.text();
        const doc = new DOMParser().parseFromString(text, 'text/html');
        const list = doc.querySelector('[data-results]');
        this.results.innerHTML = list ? list.innerHTML : '';
      } catch (err) {
        if (err.name !== 'AbortError') this.results.innerHTML = '';
      }
    }
  }
  customElements.define('predictive-search', PredictiveSearch);

  /* ------------------------------------------------------ quantity input */

  class QuantityInput extends HTMLElement {
    connectedCallback() {
      this.input = this.querySelector('input');
      this.querySelectorAll('button').forEach((btn) => {
        btn.addEventListener('click', () => {
          const step = btn.dataset.change === 'up' ? 1 : -1;
          const min = Number(this.input.min || 0);
          const next = Math.max(min, Number(this.input.value) + step);
          this.input.value = next;
          this.input.dispatchEvent(new Event('change', { bubbles: true }));
        });
      });
    }
  }
  customElements.define('quantity-input', QuantityInput);

  /* ----------------------------------------------------- variant selector */

  class VariantSelector extends HTMLElement {
    connectedCallback() {
      this.product = JSON.parse(this.querySelector('[type="application/json"]').textContent);
      this.form = this.closest('form');
      this.idInput = this.form?.querySelector('input[name="id"]');
      this.addEventListener('change', () => this.update());
      this.update();
    }

    get selectedOptions() {
      return Array.from(this.querySelectorAll('select, input:checked')).map((el) => el.value);
    }

    update() {
      const chosen = this.selectedOptions;
      const variant = this.product.variants.find((v) =>
        v.options.every((opt, i) => opt === chosen[i]));

      this.currentVariant = variant;
      if (variant && this.idInput) this.idInput.value = variant.id;

      this.updateUrl(variant);
      this.updatePrice(variant);
      this.updateButton(variant);
      this.updateMedia(variant);

      this.dispatchEvent(new CustomEvent('variant:change', { bubbles: true, detail: { variant } }));
    }

    updateUrl(variant) {
      if (!variant || this.dataset.updateUrl === 'false') return;
      const url = new URL(window.location.href);
      url.searchParams.set('variant', variant.id);
      window.history.replaceState({}, '', url);
    }

    updatePrice(variant) {
      const target = document.getElementById(this.dataset.priceTarget);
      if (!target) return;
      if (!variant) { target.innerHTML = ''; return; }

      let html = `<span class="price__current">${money(variant.price)}</span>`;
      if (variant.compare_at_price > variant.price) {
        html = `<span class="price__sale">${money(variant.price)}</span>` +
          `<s class="price__compare">${money(variant.compare_at_price)}</s>`;
      }
      target.innerHTML = html;
    }

    updateButton(variant) {
      const button = document.getElementById(this.dataset.buttonTarget);
      if (!button) return;
      const text = button.querySelector('[data-button-text]') || button;

      if (!variant) {
        button.disabled = true;
        text.textContent = window.themeStrings.unavailable;
      } else if (!variant.available) {
        button.disabled = true;
        text.textContent = window.themeStrings.soldOut;
      } else {
        button.disabled = false;
        text.textContent = window.themeStrings.addToCart;
      }

      const sku = document.getElementById(this.dataset.skuTarget);
      if (sku) sku.textContent = variant?.sku || '';
    }

    updateMedia(variant) {
      if (!variant?.featured_media) return;
      document.querySelector('product-gallery')
        ?.showMedia(String(variant.featured_media.id));
    }
  }
  customElements.define('variant-selector', VariantSelector);

  /* ------------------------------------------------------ product gallery */

  class ProductGallery extends HTMLElement {
    connectedCallback() {
      this.mainImage = this.querySelector('[data-gallery-main] img');
      this.thumbs = Array.from(this.querySelectorAll('[data-media-id]'));
      this.thumbs.forEach((thumb) => {
        thumb.addEventListener('click', () => this.showMedia(thumb.dataset.mediaId));
      });
    }

    showMedia(mediaId) {
      const thumb = this.thumbs.find((t) => t.dataset.mediaId === mediaId);
      if (!thumb || !this.mainImage) return;

      const img = thumb.querySelector('img');
      this.mainImage.src = img.dataset.full || img.src;
      this.mainImage.srcset = img.dataset.fullSrcset || '';
      this.mainImage.alt = img.alt;

      this.thumbs.forEach((t) => t.setAttribute('aria-current', String(t === thumb)));
    }
  }
  customElements.define('product-gallery', ProductGallery);

  /* ---------------------------------------------------------- cart form */

  class CartForm extends HTMLElement {
    connectedCallback() {
      this.form = this.querySelector('form');
      this.button = this.querySelector('[type="submit"]');
      this.status = this.querySelector('[data-status]');

      this.form?.addEventListener('submit', (e) => this.submit(e));
    }

    async submit(event) {
      event.preventDefault();
      this.button.disabled = true;
      if (this.status) this.status.textContent = '';

      const body = new FormData(this.form);
      body.append('sections', 'cart-icon');

      try {
        const res = await fetch(window.routes.cart_add_url, {
          method: 'POST',
          headers: { Accept: 'application/json' },
          body,
        });
        const data = await res.json();

        if (data.status) throw new Error(data.description || data.message);

        this.refreshCartCount();
        if (this.status) {
          this.status.textContent = this.dataset.addedText || 'Added to cart';
          this.status.className = 'form-status form-status--success';
        }
      } catch (err) {
        if (this.status) {
          this.status.textContent = err.message;
          this.status.className = 'form-status form-status--error';
        }
      } finally {
        this.button.disabled = false;
      }
    }

    async refreshCartCount() {
      try {
        const res = await fetch(`${window.routes.cart_url || '/cart'}.js`);
        const cart = await res.json();
        document.querySelectorAll('[data-cart-count]').forEach((el) => {
          el.textContent = cart.item_count;
          el.hidden = cart.item_count === 0;
        });
      } catch (_) { /* count refresh is cosmetic; never block the add */ }
    }
  }
  customElements.define('cart-form', CartForm);

  /* --------------------------------------------------------- cart items */

  class CartItems extends HTMLElement {
    connectedCallback() {
      this.addEventListener('change', (e) => {
        if (e.target.matches('[data-quantity]')) {
          this.updateQuantity(e.target.dataset.lineIndex, e.target.value);
        }
      });
      this.addEventListener('click', (e) => {
        const remove = e.target.closest('[data-remove]');
        if (remove) {
          e.preventDefault();
          this.updateQuantity(remove.dataset.lineIndex, 0);
        }
      });
    }

    async updateQuantity(line, quantity) {
      this.setAttribute('aria-busy', 'true');
      try {
        const res = await fetch(window.routes.cart_change_url, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({ line: Number(line), quantity: Number(quantity) }),
        });
        if (!res.ok) throw new Error('Cart update failed');
        window.location.reload();
      } catch (_) {
        window.location.reload();
      }
    }
  }
  customElements.define('cart-items', CartItems);

  /* ------------------------------------------------------- sort / facets */

  class FacetForm extends HTMLElement {
    connectedCallback() {
      this.querySelector('select')?.addEventListener('change', (e) => {
        const url = new URL(window.location.href);
        url.searchParams.set(e.target.name, e.target.value);
        url.searchParams.delete('page');
        window.location.assign(url);
      });
    }
  }
  customElements.define('facet-form', FacetForm);

  /* ------------------------------------------- app announcement bar offset

     Announcement-bar apps (Hextom QAB and friends) inject a fixed bar at the
     top of the window. Measure whatever is actually there and publish it, so
     the sticky header sits below the bar instead of under it. */

  function measureAnnouncementBar() {
    let height = 0;
    document.querySelectorAll('body > div, body > aside').forEach((el) => {
      const style = getComputedStyle(el);
      if (style.position === 'fixed' && parseFloat(style.top) === 0 && el.offsetHeight < 200) {
        height = Math.max(height, el.offsetHeight);
      }
    });
    document.documentElement.style.setProperty('--announcement-bar-height', `${height}px`);
  }

  window.addEventListener('load', measureAnnouncementBar);
  window.addEventListener('resize', measureAnnouncementBar);
  setTimeout(measureAnnouncementBar, 1200); // apps often inject late
})();
